import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';

export interface GenreStats {
  genre: string;
  count: number;
  averageRating: number;
  totalPages: number;
  averageDays: number;
}

interface GenreDashboardProps {
  genreStats: GenreStats[];
}

export function GenreDashboard({ genreStats }: GenreDashboardProps) {
  if (genreStats.length === 0) return null;

  // Prepare data for radar chart (top 6 genres by count)
  const topGenres = genreStats.slice(0, 6);
  const radarData = topGenres.map(genre => ({
    genre: genre.genre.length > 15 ? genre.genre.substring(0, 12) + '...' : genre.genre,
    value: genre.count,
    fullGenre: genre.genre,
  }));

  const chartConfig = {
    value: {
      label: "Books Read",
      color: "hsl(var(--primary))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Genre Distribution Radar Chart */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Genre Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">
            Your reading spread across different genres
          </p>
          
          <ChartContainer config={chartConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis 
                  dataKey="genre" 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, Math.max(...radarData.map(d => d.value))]}
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <Radar 
                  name="Books" 
                  dataKey="value" 
                  stroke="hsl(var(--primary))" 
                  fill="hsl(var(--primary))" 
                  fillOpacity={0.3}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                  formatter={(value: number, name: string, props: any) => [
                    `${value} books`,
                    props.payload.fullGenre
                  ]}
                />
              </RadarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Detailed Genre Stats */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-accent" />
            Genre Deep Dive
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Detailed statistics for each genre you explored
          </p>
          
          <div className="space-y-4">
            {genreStats.slice(0, 8).map((genre, index) => (
              <div key={genre.genre} className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {index + 1}
                      </div>
                      <span className="font-medium">{genre.genre}</span>
                    </div>
                    
                    <div className="pl-8 flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {genre.count} book{genre.count !== 1 ? 's' : ''}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        ⭐ {genre.averageRating.toFixed(1)} avg rating
                      </Badge>
                      {genre.averageDays > 0 && (
                        <Badge variant="outline" className="text-xs">
                          📅 {genre.averageDays} days avg
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        📄 {genre.totalPages.toLocaleString()} pages
                      </Badge>
                    </div>
                  </div>
                </div>
                {index < genreStats.length - 1 && index < 7 && (
                  <div className="border-b border-border/50" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
